import { useCloseDeletedSelection } from "../../components/common/useCloseDeletedSelection";
import { useEffect, useMemo, useRef, useState } from "react";
import { createFormValidation } from "../../../helpers/formValidationHelper";
import Loading from "../../components/common/Loading";
import { ILanguage, ISiteDetails, IStaticPage } from "../../../models/Interfaces";
import { ContentService } from "../../../services/ContentService";
import { StaticPageService } from "../../../services/StaticPageService";
import { Button, Col, Form, FormControl, FormLabel, FormSelect, Row } from "react-bootstrap";
import FormCheckInput from "react-bootstrap/esm/FormCheckInput";
import { CellProps, Column } from "react-table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faPlus, faSave, faTimes } from "@fortawesome/free-solid-svg-icons";
import FilteredTable from "../../components/common/FilteredTable";
import { useConfirm } from "../../../config/ConfirmProvider";
import AppModal from "../../components/common/AppModal";
import { Core } from "@form-validation/bundle/full";
import { Bootstrap5 } from "@form-validation/plugin-bootstrap5";
import { Trigger } from "@form-validation/plugin-trigger";
import { SiteService } from "../../../services/SiteService";
import AppEmptyState from "../../components/common/AppEmptyState";
import styles from "./staticPages.module.scss";

const StaticPages = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [languages, setLanguages] = useState<Array<ILanguage>>([]);
  const [selectedLanguage, setSelectedLanguage] = useState<string | null>(null);
  const [staticPages, setStaticPages] = useState<Array<IStaticPage>>([]);
  const [selectedItem, setSelectedItem] = useState<IStaticPage | null>(null);
  const [showDetail, setShowDetail] = useState<boolean>(false);
  const [siteDetails, setSiteDetails] = useState<ISiteDetails | null>(null);
  
  const contentSvcRef = useRef<ContentService | null>(null);
  const siteSvcRef = useRef<SiteService | null>(null);
  const staticSvcRef = useRef<StaticPageService | null>(null);
  const spFormRef = useRef<HTMLFormElement | null>(null);
  const spFvref = useRef<Core | null>(null);

  const confirmation = useConfirm();

  const columns: Column<IStaticPage>[] = useMemo(() => [
    {
      Header: "Site Language",
      Cell: ({ row }: CellProps<IStaticPage>) => (
        <span>{getLanguageName(row.original.Language)}</span>
      )
    },
    {
      Header: "Incoming Url (this site url)",
      id: "Url",
      Cell: ({ row }: CellProps<IStaticPage>) => (
        <span className={styles.lowercaseValue}>{row.original.Url?.toLowerCase() ?? ""}</span>
      )
    },
    {
      Header: "Static Path (external or static file)",
      id: "StaticPagePath",
      Cell: ({ row }: CellProps<IStaticPage>) => (
        <span className={styles.lowercaseValue}>{row.original.StaticPagePath?.toLowerCase() ?? ""}</span>
      )
    },
    {
      Header: "Enabled",
      Cell: ({ row }: CellProps<IStaticPage>) => {
        const { Id, Enabled } = row.original;
        const newValue = !Enabled;

        return (
          <span
            className={styles.statusCell}
            title={Enabled ? "click to disable" : "click to enable"}
            onClick={() => handleTableItemChange(Id, "Enabled", newValue)}
          >
            <span className={Enabled ? styles.statusBadgeOn : styles.statusBadgeOff}>
              {Enabled ? "Enabled" : "Disabled"}
            </span>
          </span>
        );
      }
    },
    {
      Header: "Include On Sitemap",
      Cell: ({ row }: CellProps<IStaticPage>) => {
        const { Id, IncludeOnSiteMap } = row.original;
        const newValue = !IncludeOnSiteMap;

        return (
          <span
            className={styles.statusCell}
            title={IncludeOnSiteMap ? "click to remove from sitemap" : "click to include on sitemap"}
            onClick={() => handleTableItemChange(Id, "IncludeOnSiteMap", newValue)}
          >
            <span className={IncludeOnSiteMap ? styles.statusBadgeOn : styles.statusBadgeOff}>
              {IncludeOnSiteMap ? "Included" : "Excluded"}
            </span>
          </span>
        );
      }
    },
    {
      id: "Actions",
      Header: "",
      Cell: ({ row }: CellProps<IStaticPage>) => (
        <div className="user-result-actions">
          <Button variant="light" onClick={() => editStaticPage(row.original)} className="user-result-actions__btn user-result-actions__btn--view" title="Edit static page">
            <FontAwesomeIcon icon={faEdit} />
          </Button>
          <Button variant="light" onClick={() => deleteStaticPage(row.original)} className="user-result-actions__btn user-result-actions__btn--danger" title="Delete static page">
            <FontAwesomeIcon icon={faTimes} />
          </Button>
        </div>
      )
    }
  ], [languages]);

  const getSiteDetails = async () : Promise<void> => {
    if (siteSvcRef.current) {
      const siteres = await siteSvcRef.current.getSiteDetails();
      if (siteres) {
        setSiteDetails(siteres);
      }
    }
  };

  const getLanguages = async (): Promise<void> => {
    if (contentSvcRef.current) {
      const res = await contentSvcRef.current.getSiteLanguages();
      if (res.length > 0) {
        setLanguages(res);
      }
    }
  };

  const getStaticPages = async (shortcode: string): Promise<void> => {
    if (shortcode && staticSvcRef.current) {
      const res = await staticSvcRef.current.getStaticPages(shortcode);
      setStaticPages(res);
    }
  };

  const selectLanguage = (event: React.ChangeEvent<HTMLSelectElement>): void => {
    const value = event.target.value;
    if (value) {
      setSelectedLanguage(value);
    }
  };

  const getLanguageName = (shortcode: string): string => {
    if (languages) {
      const lang = languages.find(l => l.ShortCode.toLowerCase() === shortcode.toLowerCase());
      return lang?.Language ?? shortcode;
    }
    return shortcode;
  };

  const addStaticPage = (): void => {
    if (staticSvcRef.current) {
      const item = staticSvcRef.current.newStaticPage(selectedLanguage ?? "en-gb");
      if (item) {
        setSelectedItem(item);
        setShowDetail(true);
      }
    }
  };

  const editStaticPage = (item: IStaticPage): void => {
    if (item) {
      setSelectedItem(item);
      setShowDetail(true);
    }
  };

  const saveStaticPage = async (): Promise<void> => {
    if (staticSvcRef.current && selectedItem && spFvref.current) {
      const validres = await spFvref.current.validate();

      if (validres === "Valid") {
        const res = await staticSvcRef.current.saveStaticPage(selectedItem);
        if (res) {
          if (res) {
            setStaticPages(prev =>
              prev.some(sp => sp.Id === res.Id)
                ? prev.map(sp => (sp.Id === res.Id ? res : sp))
                : [...prev, res]
            );
          }
        }
        closeDetail();
      }
    }
  };

  const closeDeletedSelection = useCloseDeletedSelection(selectedItem, (item) => item.Id, () => closeDetail());

  const deleteStaticPage = async (item: IStaticPage): Promise<void> => {
    if (item && staticSvcRef.current) {
      const confirmed = await confirmation.requestConfirmation(`Delete static page for incoming url <u>${item.Url}</u>?<br /><b>This action cannot be undone.</b>`, "Delete static page");

      if (confirmed) {
        const res = await staticSvcRef.current.deleteStaticPage(item.Id);
        if (res) {
          setStaticPages(prev => prev.filter(sp => sp.Id !== item.Id));
          closeDeletedSelection(item.Id);
        }
      }
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    if (selectedItem) {
      const { name, value } = event.target;

      if (name) {
        updateItemProperty(name, value);
      }
    }
  };

  const handleTableItemChange = async (itemid: number, name: keyof IStaticPage, value: any): Promise<void> => {
    if (staticSvcRef.current) {
      const item = staticPages.find((sp) => sp.Id === itemid);

      if (item) {
        const updatedItem = {
          ...item,
          [name]: value
        };

        const res = await staticSvcRef.current.saveStaticPage(updatedItem);

        if (res) {
          setStaticPages(prev =>
            prev.map(sp => (sp.Id === res.Id ? res : sp))
          );
        }
      }
    }
  };

  const updateItemProperty = (name: string, value: any): void => {
    if (selectedItem && name) {
      setSelectedItem(prev => ({
        ...prev as IStaticPage,
        [name]: value
      }));
    }
  };

  const createValidator = (): void => {
    if (staticSvcRef.current && spFormRef.current && !spFvref.current) {
      spFvref.current = createFormValidation(spFormRef.current, {
        fields: {
          Url: {
            validators: {
              notEmpty: { message: "A site url is required" }
            }
          },
          StaticPagePath: {
            validators: {
              notEmpty: { message: "A target URL is required" }
            }
          }
        },
        plugins: {
          bootstrap5: new Bootstrap5(),
          trigger: new Trigger()
        }
      });
    }
  };

  const closeDetail = (): void => {
    setSelectedItem(null);
    setShowDetail(false);
  };

  useEffect(() => {
    if (spFormRef.current) {
      createValidator();
    } else {
      spFvref.current = null;
    }
  }, [spFormRef, showDetail]);

  useEffect(() => {
    if (selectedLanguage) {
      getStaticPages(selectedLanguage);
    }
  }, [selectedLanguage]);

  useEffect(() => {
    if (siteDetails && siteDetails.DefaultLanguage) {
      setSelectedLanguage(siteDetails.DefaultLanguage);
    }
  }, [siteDetails]);

  useEffect(() => {
    if (!contentSvcRef.current) {
      contentSvcRef.current = new ContentService();
    }

    if (!staticSvcRef.current) {
      staticSvcRef.current = new StaticPageService();
    }

    if (!siteSvcRef.current) {
      siteSvcRef.current = new SiteService();
    }

    const getData = async () => {
      try {
        await Promise.all([
          getLanguages(),
          getSiteDetails()
        ]);
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    }
    getData();
  }, []);

  return (
    <>
      {loading && (
        <Loading Message="Loading static pages...." SpinSize="5x" />
      )}

      {!loading && (
        <div className={styles.page} id="static-pages">
          <section className="app-list-section">
            <div className="app-list-section__header">
              <div className={styles.sectionLead}>
                <div>
                  <h2 className="app-list-section__title">Route Library</h2>
                </div>
                <div className={styles.languagePicker}>
                  <FormLabel>Select site language</FormLabel>
                  <FormSelect
                    id="SiteLanguage"
                    name="SiteLanguage"
                    title="select site language"
                    value={selectedLanguage ?? ""}
                    onChange={selectLanguage}>
                    <option key="lang-default" value="" disabled>Select site language...</option>
                    {languages.map(l => (
                      <option key={`lang-${l.ShortCode}`} value={l.ShortCode}>{l.Language}</option>
                    ))}
                  </FormSelect>
                </div>
              </div>
            </div>
            <div className="app-list-section__body">
            {selectedLanguage ? (
              staticPages.length > 0 ? (
                <FilteredTable<IStaticPage>
                  columns={columns}
                  data={staticPages}
                  noDataMessage="No static routes found for this language."
                  showAddButton={true}
                  addButtonText="Add static page"
                  addButtonHandler={addStaticPage}
                  filterPlaceHolder="filter routes..."
                />
              ) : (
                <div className={styles.emptyWrap}>
                  <AppEmptyState
                    title="No static routes found"
                    message="Create a static route for this language to map an incoming site URL to a static file or external destination."
                    compact
                  />
                  <div className={styles.emptyActions}>
                    <Button variant="outline-primary" onClick={addStaticPage}>
                      <FontAwesomeIcon icon={faPlus} /> Add static page
                    </Button>
                  </div>
                </div>
              )
            ) : (
              <div className={styles.emptyWrap}>
                <AppEmptyState
                  title="No language selected"
                  message="Choose a site language to review and manage the static routes available for that version of the site."
                  compact
                />
              </div>
            )}
            </div>
          </section>
        </div>
      )}

      {showDetail && selectedItem && (
        <AppModal
          onClose={closeDetail}
          showButtons={true}
          size="md"
          actionButtonVariant="success"
          cancelButtonVariant="primary"
          cancelButtonText="Cancel"
          dataActionButtonText="Save"
          actionButtonIcon={faSave}
          title="Static Page Setting"
          onDataAction={() => saveStaticPage()}>
          <Form id="static-page-form" ref={spFormRef}>
            <Row>
              <Col md={12}>
                <FormLabel htmlFor="Url">Site URL</FormLabel>
                <FormControl
                  type="text"
                  value={selectedItem.Url}
                  onChange={handleInputChange}
                  id="Url"
                  name="Url"
                  placeholder="your site url"
                  autoComplete="off"
                />
              </Col>
            </Row>
            <Row>
              <Col md={12} className="mt-3">
                <FormLabel htmlFor="StaticPagePath">Static Page Path (stored or external url)</FormLabel>
                <FormControl
                  type="text"
                  value={selectedItem.StaticPagePath}
                  onChange={handleInputChange}
                  id="StaticPagePath"
                  name="StaticPagePath"
                  placeholder="url to stored page or external site"
                  autoComplete="off"
                />
              </Col>
            </Row>
            <Row>
              <Col md={12} lg={6} className="mt-3">
                <FormLabel htmlFor="Enabled">Enabled</FormLabel>
                <FormCheckInput
                  className="form-check"
                  checked={selectedItem.Enabled}
                  onChange={(e) => updateItemProperty("Enabled", e.target.checked)}
                  name="Enabled"
                  id="Enabled"
                  title="enable or disable this static page"
                />
              </Col>
              <Col md={12} lg={6} className="mt-3">
                <FormLabel htmlFor="IncludeOnSiteMap">Include On Sitemap</FormLabel>
                <FormCheckInput
                  className="form-check"
                  checked={selectedItem.IncludeOnSiteMap}
                  onChange={(e) => updateItemProperty("IncludeOnSiteMap", e.target.checked)}
                  name="IncludeOnSiteMap"
                  id="IncludeOnSiteMap"
                  title="include or exclude details from sitemap"
                />
              </Col>
            </Row>
          </Form>
        </AppModal>
      )}
    </>
  );
}

export default StaticPages;
